import { useQuery } from '@apollo/client/react';
import { Search, SlidersHorizontal } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FetchResultsPrograms } from '../../AssessmentResultsPage/shared/Result.graphql';
import type { PerformanceFilterState } from './utils';
import { buildAssessmentRunOptions } from './utils';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import Select from '@/components/common/Select';
import { useActiveRole, useUser } from '@/contexts/UserSession';
import { AssessmentRunStatus, UserRole } from '@/gql/enums';
import type { ResultsProgramItemDataFragment } from '@/gql/graphql';
import useCompanyRoleLabels from '@/utils/useCompanyRoleLabels';

export interface PerformanceResultListFilterProps {
    filterState: PerformanceFilterState;
    callback: (state: PerformanceFilterState) => void;
}

const PerformanceResultListFilter = ({ filterState, callback }: PerformanceResultListFilterProps) => {
    const { t } = useTranslation('performanceResult');
    const { t: commonTranslation } = useTranslation('common');
    const user = useUser(true);
    const activeRole = useActiveRole();
    const isTrainee = activeRole && activeRole.role === UserRole.Trainee;
    const { getRoleLabel } = useCompanyRoleLabels();

    const [searchValue, setSearchValue] = useState<string>(filterState.traineeName ?? '');

    useEffect(() => {
        setSearchValue(filterState.traineeName ?? '');
    }, [filterState.traineeName]);

    const [showFilters, setShowFilters] = useState(true);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { data } = useQuery(FetchResultsPrograms, {
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: { includeAssessmentRunStatuses: [AssessmentRunStatus.Ended, AssessmentRunStatus.InProgress] },
        },
    });

    const programs: ResultsProgramItemDataFragment[] = useMemo(
        () => data?.programs?.items || [],
        [data?.programs?.items]
    );

    const programOptions = useMemo(() => {
        const filteredPrograms = programs.map(item => ({
            value: item.id,
            label: item.name,
            assessmentRunIds: item.assessmentRuns?.map(ar => ar.id) || [],
        }));

        return filteredPrograms;
    }, [programs]);

    const assessmentRunOptions = useMemo(
        () =>
            buildAssessmentRunOptions(
                programs.filter(p => !filterState.programId || p.id === filterState.programId),
                {
                    groupId: filterState.groupId,
                    subGroupId: filterState.subGroupId,
                }
            ),
        [filterState.groupId, filterState.subGroupId, filterState.programId, programs]
    );

    const groupOptions = useMemo(() => {
        const groups: { value: string; label: string }[] = [];

        programs
            .filter(p => !filterState.programId || p.id === filterState.programId)
            .forEach(program => {
                program.assessmentRuns?.forEach(run => {
                    if (run.group) {
                        const exists = groups.find(g => g.value === run.group.id);
                        if (!exists) {
                            groups.push({
                                value: run.group.id,
                                label: run.group.name,
                            });
                        }
                    }
                });
            });

        return groups;
    }, [filterState.programId, programs]);

    const subgroupOptions = useMemo(() => {
        const subgroups: { value: string; label: string; groupId: string | null }[] = [];

        programs
            .filter(p => !filterState.programId || p.id === filterState.programId)
            .forEach(program => {
                program.assessmentRuns?.forEach(run => {
                    const groupId = run.group?.id ?? null;

                    run.subGroups?.forEach(sg => {
                        if (filterState.groupId && groupId !== filterState.groupId) {
                            return;
                        }

                        const existing = subgroups.find(s => s.value === sg.id);
                        if (!existing) {
                            subgroups.push({
                                value: sg.id,
                                label: sg.name,
                                groupId,
                            });
                        }
                    });
                });
            });

        return subgroups;
    }, [programs, filterState.programId, filterState.groupId]);

    const emitChange = useCallback(
        (next: Partial<PerformanceFilterState> = {}) => {
            callback({
                ...filterState,
                traineeName: searchValue || null,
                ...next,
            });
        },
        [callback, filterState, searchValue]
    );

    const onGroupChange = useCallback(
        (value: string | number | null) => {
            const groupId = value?.toString() || null;
            // Reset subgroup and assessment run when group changes
            emitChange({
                groupId,
                subGroupId: null,
                assessmentRunId: null,
            });
        },
        [emitChange]
    );

    const onProgramChange = useCallback(
        (value: string | number | null) => {
            const selectedProgram = programOptions.find(p => p.value === value);
            if (!selectedProgram) {
                return;
            }

            emitChange({
                programId: value?.toString() || undefined,
                filterAssessmentRunIds: selectedProgram.assessmentRunIds,
                assessmentRunId: undefined,
                groupId: undefined,
                subGroupId: undefined,
            });
        },
        [emitChange, programOptions]
    );

    const onAssessmentRunChange = useCallback(
        (value: string | number | null) => {
            const runId = value?.toString() || null;
            const run = assessmentRunOptions.find(ar => ar.value === runId);

            emitChange({
                assessmentRunId: runId,
                filterAssessmentRunIds: runId ? [runId] : [],
                groupId: run?.groupId ?? filterState.groupId,
            });
        },
        [assessmentRunOptions, emitChange, filterState.groupId]
    );

    const onSubgroupChange = useCallback(
        (value: string | number | null) => {
            const subgroupId = value?.toString() || null;
            const subgroup = subgroupOptions.find(s => s.value === subgroupId);

            emitChange({
                subGroupId: subgroupId,
                groupId: subgroup?.groupId ?? filterState.groupId,
                assessmentRunId: null,
            });
        },
        [emitChange, subgroupOptions, filterState.groupId]
    );

    const onSearchClick = useCallback(() => {
        emitChange();
    }, [emitChange]);

    const onClearFilter = useCallback(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        setSearchValue('');
        emitChange({
            traineeName: null,
            groupId: null,
            subGroupId: null,
            assessmentRunId: null,
            programId: null,
            filterAssessmentRunIds: [],
        });
    }, [emitChange]);

    const onSearchChange = useCallback(
        (value: string) => {
            setSearchValue(value);
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
            searchDebounceRef.current = setTimeout(() => {
                emitChange();
            }, 300);
        },
        [emitChange]
    );

    const onSearchKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                if (searchDebounceRef.current) {
                    clearTimeout(searchDebounceRef.current);
                }
                emitChange();
            }
        },
        [emitChange]
    );

    const calculatedSelectedDropdown = useMemo(() => {
        let count = 0;
        if (filterState.groupId) {
            count += 1;
        }
        if (filterState.subGroupId) {
            count += 1;
        }
        if (filterState.assessmentRunId) {
            count += 1;
        }
        if (filterState.traineeName) {
            count += 1;
        }
        return count;
    }, [filterState.groupId, filterState.subGroupId, filterState.assessmentRunId, filterState.traineeName]);

    const isAdmin = useMemo(() => {
        return (
            (activeRole && (activeRole.role === UserRole.CourseAdmin || activeRole.role === UserRole.CompanyAdmin)) ||
            user.isSuperAdmin
        );
    }, [activeRole, user]);
    return (
        <div className="grid items-end gap-4 lg:grid-cols-[repeat(4,1fr)_120px]">
            {!isTrainee && (
                <div className="mb-2 grid gap-4 lg:col-span-5 lg:grid-cols-[repeat(4,1fr)_120px]">
                    <div className="lg:col-span-2">
                        <Input
                            name="trainee"
                            label={t('filter.search')}
                            onSuffixClick={onSearchClick}
                            suffixElement={<Search className="size-4 text-neutral-500" strokeWidth={2} />}
                            value={searchValue}
                            onChange={e => onSearchChange(e.target.value)}
                            onKeyDown={onSearchKeyDown}
                            placeholder={t('filter.searchPlaceholder', {
                                role: getRoleLabel(UserRole.Trainee).toLocaleLowerCase(),
                            })}
                        />
                    </div>
                    <div className="flex justify-self-end text-center text-base font-semibold text-blue-600 lg:col-span-3 lg:px-4">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="flex cursor-pointer items-center gap-1"
                        >
                            <SlidersHorizontal className="size-4" />
                            <span>{calculatedSelectedDropdown}</span>
                            <span>
                                {calculatedSelectedDropdown === 1
                                    ? commonTranslation('filter.filter')
                                    : commonTranslation('filter.filters')}
                            </span>
                        </button>
                    </div>
                </div>
            )}

            {showFilters && !isAdmin && (
                <>
                    <div className="lg:col-span-2">
                        <Select
                            name="program"
                            label={t('filter.program')}
                            placeholder={t('filter.programPlaceholder')}
                            options={programOptions}
                            value={filterState.programId || null}
                            onChange={onProgramChange}
                        />
                    </div>
                    <div className="lg:col-span-2">
                        <Select
                            name="assessmentRun"
                            label={t('filter.assessmentRun')}
                            placeholder={t('filter.assessmentRunPlaceholder')}
                            options={assessmentRunOptions}
                            value={filterState.assessmentRunId || null}
                            onChange={onAssessmentRunChange}
                        />
                    </div>
                    <div>
                        <Button
                            onClick={onClearFilter}
                            className="min-w-[120px]"
                            variant="secondary"
                            disabled={calculatedSelectedDropdown === 0}
                        >
                            {commonTranslation('filter.clearAll')}
                        </Button>
                    </div>
                </>
            )}

            {showFilters && isAdmin && (
                <>
                    <Select
                        name="program"
                        label={t('filter.program')}
                        placeholder={t('filter.programPlaceholder')}
                        options={programOptions}
                        value={filterState.programId || null}
                        onChange={onProgramChange}
                        className="col-span-5 lg:col-span-1"
                    />

                    <Select
                        name="group"
                        label={t('filter.userGroup')}
                        placeholder={t('filter.userGroupPlaceholder')}
                        options={groupOptions}
                        value={filterState.groupId || null}
                        onChange={onGroupChange}
                        className="col-span-5 lg:col-span-1"
                    />
                    <Select
                        name="subgroup"
                        label={t('filter.subgroup')}
                        placeholder={t('filter.subgroupPlaceholder')}
                        options={subgroupOptions}
                        value={filterState.subGroupId || null}
                        onChange={onSubgroupChange}
                        className="col-span-5 lg:col-span-1"
                    />
                    <Select
                        name="assessmentRun"
                        label={t('filter.assessmentRun')}
                        placeholder={t('filter.assessmentRunPlaceholder')}
                        options={assessmentRunOptions}
                        value={filterState.assessmentRunId || null}
                        onChange={onAssessmentRunChange}
                        className="col-span-5 lg:col-span-1"
                    />

                    <div>
                        <Button
                            onClick={onClearFilter}
                            className="min-w-[120px]"
                            variant="secondary"
                            disabled={calculatedSelectedDropdown === 0}
                        >
                            {commonTranslation('filter.clearAll')}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default PerformanceResultListFilter;
